<script setup lang="ts">
import Button from "@/components/Reusables/Buttons/Button.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import SelectInputWithKeyValue from "@/components/Reusables/Inputs/SelectInputWithKeyValue.vue";
import type { WasteMaterial } from "@/api/services/waste/types";
import type { WasteTypeGroup } from "@/pages/WasteSorting/types/types";
import { useProjectEditReadOnly } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";
import RevisionGatedButton from "@/pages/ProjectVersionHistory/components/revisions/RevisionGatedButton.vue";
import { useModalStore } from "@/stores/modals/modals.store";
import format from "@/services/Utils/format";
import { computed, ref } from "vue";

export type MultiDropEntry = {
  material: WasteMaterial;
  weight?: { original: number; scaled: number };
};

const props = defineProps<{
  materials: MultiDropEntry[];
  group: WasteTypeGroup;
  onSave: (entries: { material: WasteMaterial; weight: number }[]) => Promise<void>;
  weightUnit?: "kg" | "ton";
}>();

const modals = useModalStore();

// Same guard as the single-material drop modal: both ways in are blocked for a Viewer and while a
// frozen revision is previewed, so this covers the modal left open across a change in either.
const { isReadOnly: isRevisionReadOnly, blockedByPreview } = useProjectEditReadOnly();

// Weights are stored in kg; the page-level kg/ton switch controls how we display them here.
const isTon = computed(() => props.weightUnit === "ton");
const unitLabel = computed(() => (isTon.value ? "ton" : "kg"));
const toDisplay = (kg: number) => (isTon.value ? kg / 1000 : kg);
const toKg = (display: number) => (isTon.value ? display * 1000 : display);

const weight = ref<number | null>(null);
const percentage = ref<number | null>(null);
const isSaving = ref(false);
const unitOptions = computed(() => [{ name: unitLabel.value, value: unitLabel.value }]);
const selectedUnit = ref(unitLabel.value);

const totalCombinedWeight = computed(() => props.materials.reduce((sum, m) => sum + (m.weight?.original ?? 0), 0));
const displayTotalCombinedWeight = computed(() => toDisplay(totalCombinedWeight.value));

const saveDisabled = computed(() => !weight.value || weight.value <= 0);

const handleWeightChange = (newWeight: number | null) => {
  weight.value = newWeight;
  if (totalCombinedWeight.value > 0 && newWeight !== null) {
    percentage.value = parseFloat(((toKg(newWeight) / totalCombinedWeight.value) * 100).toFixed(2));
  } else {
    percentage.value = null;
  }
};

const handlePercentageChange = (newPercentage: number | null) => {
  percentage.value = newPercentage;
  if (totalCombinedWeight.value > 0 && newPercentage !== null) {
    weight.value = parseFloat(toDisplay((newPercentage / 100) * totalCombinedWeight.value).toFixed(2));
  } else {
    weight.value = null;
  }
};

const handleSave = async () => {
  if (blockedByPreview()) return;
  if (saveDisabled.value || !weight.value) return;
  isSaving.value = true;
  try {
    const totalWeightKg = toKg(weight.value);
    // Distribute total weight proportionally among selected materials based on each material's share
    const entries = props.materials.map((m) => {
      const materialWeight = m.weight?.original ?? 0;
      const proportion = totalCombinedWeight.value > 0 ? materialWeight / totalCombinedWeight.value : 1 / props.materials.length;
      return {
        material: m.material,
        weight: parseFloat((proportion * totalWeightKg).toFixed(2)),
      };
    });
    await props.onSave(entries);
    modals.closeByName("WasteMultipleMaterialDropModal");
  } finally {
    isSaving.value = false;
  }
};

const handleCancel = () => {
  modals.closeByName("WasteMultipleMaterialDropModal");
};
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <h2 class="t-sh4">Add waste sorting to {{ group.wasteType.label }}</h2>

    <!-- Summary banner -->
    <div class="rounded-8 t-small bg-app-blue-50 text-app-blue-800 flex items-start gap-3 px-4 py-3">
      <font-awesome-icon
        icon="fa-solid fa-layer-group"
        class="t-body mt-0.5 shrink-0" />
      <span>
        <strong>{{ materials.length }} materials</strong> selected with a combined weight of
        <strong>{{ format.decimalNumber(displayTotalCombinedWeight, 2) }} {{ unitLabel }}</strong
        >. The weight you enter will be distributed proportionally among them.
      </span>
    </div>

    <!-- Materials list -->
    <div class="rounded-8 t-xs max-h-40 overflow-y-auto border border-slate-200">
      <div
        v-for="m in materials"
        :key="m.material.id"
        class="flex items-center justify-between gap-2 px-3 py-1.5 odd:bg-slate-50">
        <span class="t-caption truncate text-slate-700">{{ m.material.name }}</span>
        <span class="shrink-0 text-slate-500"
          >{{ format.decimalNumber(toDisplay(m.weight?.original ?? 0), 2) }} {{ unitLabel }}</span
        >
      </div>
    </div>

    <!-- Weight inputs -->
    <div class="grid grow grid-cols-[1fr_auto_auto_auto] gap-3">
      <NumberInput
        :value="weight"
        label="Total Weight"
        placeholder="0"
        color="primary"
        :positive="true"
        :max="totalCombinedWeight > 0 ? displayTotalCombinedWeight : undefined"
        :disabled="isRevisionReadOnly"
        @update:value="handleWeightChange" />
      <SelectInputWithKeyValue
        :value="selectedUnit"
        :options="unitOptions"
        label="Unit"
        :is-disabled="isRevisionReadOnly"
        @update:value="selectedUnit = $event" />
      <div class="flex h-10 items-center self-end">
        <div class="h-6 w-px bg-slate-200"></div>
      </div>
      <NumberInput
        :value="percentage"
        label="Percentage"
        placeholder="0"
        color="primary"
        :max="100"
        :positive="true"
        :max-decimals="2"
        :disabled="isRevisionReadOnly"
        class="text-slate-600"
        @update:value="handlePercentageChange" />
    </div>

    <div
      v-if="totalCombinedWeight > 0"
      class="t-xs flex items-center justify-end py-2 text-slate-500">
      <span>
        Total combined weight:
        <strong class="text-slate-700">{{ format.decimalNumber(displayTotalCombinedWeight, 2) }} {{ unitLabel }}</strong>
      </span>
    </div>

    <div class="mt-auto flex justify-between">
      <Button
        label="Cancel"
        color="button"
        :disabled="isSaving"
        @click="handleCancel" />
      <RevisionGatedButton
        :label="isSaving ? 'Saving...' : 'Save'"
        :color="saveDisabled || isSaving ? 'disabled' : 'primary'"
        :disabled="saveDisabled || isSaving"
        :is-loading="isSaving"
        :guard="useProjectEditReadOnly"
        @click="handleSave" />
    </div>
  </div>
</template>
